import root from "window-or-global";
import {isEmpty, isString, throttle, truncate, take} from "lodash/fp";
import dayjs from "dayjs";
import stringify from "json-stringify-safe";
import {LOG_SERVICE_URL} from "@atg-shared/service-url";
import atgFetch from "@atg-shared/fetch";
import {AtgRequestError} from "@atg-shared/fetch-types";

/**
 * The data format for a temporarily buffered single log entry, while it's waiting to be sent.
 */
type BufferedLogEntry = {
    logLevel: string;
    message: string;
    clientTimestamp: string;
    extraInfo: Record<string, unknown>;

    /**
     * The micro frontend that the log was created in
     *
     * This is used to setup per-microFE log dashboards, to keep the log volume manageable.
     */
    currentMicroFE?: string;
};

/** how long to buffer logs before POST:ing them to backend */
const LOG_INTERVAL = 10 * 1000;

let bufferedLogEntries: Array<BufferedLogEntry> = [];

/**
 * This helper function maps from "client log level names" to "server log level names"
 *
 * Concretely speaking the server endpoint will reject the `warn` loglevel, it should be named
 * `warning` instead. Note that this is also what you should filter for in Splunk instead of `warn`.
 */
const renameLogLevel = (logLevelClientName: string) => {
    if (logLevelClientName === "warn") return "warning";
    return logLevelClientName;
};

/**
 * Format each individual log message according to the API contract. The `category`, `level` and
 * `content` fields are mandatory. `content` can be any string – stringified JSON in our case.
 */
const formatLogPayload = (logs: Array<BufferedLogEntry> = []) =>
    stringify(
        logs.map(({logLevel, message, clientTimestamp, currentMicroFE, extraInfo}) => ({
            category: "client.log",
            level: renameLogLevel(logLevel),
            content: stringify({
                url: root.location?.href,
                clientVersions: root.clientVersions,
                env: root.clientConfig?.env,
                microFE: currentMicroFE,
                // Since we throttle `logToServer` and send a batch of logs at the same time to the
                // server, it can't know the precise time between events in the same batch (only the
                // order). Therefore we add a client timestamp as well.
                clientTimestamp,

                ...(isString(message) ? {message} : message),
                ...(isString(extraInfo) ? {extraInfo} : extraInfo),
            }),
        })),
    );

root.document?.addEventListener("visibilitychange", () => {
    // only modern browsers support `sendBeacon`
    if (!navigator.sendBeacon) return;

    // happy path – no logs to send
    if (isEmpty(bufferedLogEntries)) return;

    // ideally we'd only run this logic when the user actually leaves the page, but using the
    // "unload" event in combination with `sendBeacon` is not recommended
    // (see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon and https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event)
    if (document.visibilityState !== "hidden") return;

    // Send data without potentially waiting for the `LOG_INTERVAL` delay if the page is becoming
    // hidden. This is especially targeted at the scenario when the user closes her tab before we
    // have sent our accumulated logs to our log infrastructure (Splunk). Note that there's no retry
    // logic here, unlike the normal log posting flow (`sendBeacon` doesn't support it).
    navigator.sendBeacon(LOG_SERVICE_URL, formatLogPayload(bufferedLogEntries));
    bufferedLogEntries = [];
});

/**
 * Sometimes our POST request to store log fails. This could be because of network issues, backend
 * issues, or commonly, because the log is too big (at the time of writing the backend endpoint will
 * reject any message larger than 100 KB). When this happens we truncate our log entries and retry
 * once, so we're not completely left in the dark.
 */
const truncateLogEntries = (logs: Array<BufferedLogEntry> = []) =>
    logs.map(
        (logEntry: BufferedLogEntry) => ({
            level: logEntry.logLevel,
            message: truncate({length: 30}, logEntry.message),
        }),
        take(4, logs),
    );

const logToServer = async () => {
    if (isEmpty(bufferedLogEntries)) return;

    // While we're making our POST request new entries might be added to `bufferedLogEntries`, but
    // it's too late to include those. Clear the global variable here, but keep a local reference to
    // the items (in case we need to retry, for example).
    const entriesToPost = bufferedLogEntries;
    bufferedLogEntries = [];

    try {
        await atgFetch(LOG_SERVICE_URL, {
            method: "POST",
            body: formatLogPayload(entriesToPost),
        });
    } catch (err: unknown) {
        if (!(err instanceof AtgRequestError)) return;

        // If our POST fails (e.g. because the payload is too large), log that error so we're not in
        // the dark.
        //
        // Note that we intentionally don't reuse `log.error()` or `addLogEntry()` here, since that
        // could potentially lead to recursion and DDoS:ing ourselves.
        try {
            await atgFetch(LOG_SERVICE_URL, {
                method: "POST",
                body: formatLogPayload([
                    {
                        logLevel: "error",
                        message: "Error posting logs",
                        clientTimestamp: dayjs().toISOString(),
                        extraInfo: {
                            // we're obviously very constrained with size here (since the most likely
                            // reason we're in `catch` here is because we tried to send more data than
                            // allowed), but it's very valuable to at least get a hint of what the
                            // original message was
                            truncatedMessages: truncateLogEntries(entriesToPost),

                            // we only sent a small piece of the latest 4 logs, but maybe more were
                            // buffered, so include a total count
                            lostLogs: entriesToPost.length,
                            response: err.response,
                        },
                    },
                ]),
            });
        } catch (innerErr: unknown) {
            console.error("Failed posting logs", {error: innerErr, entriesToPost});
        }
    }
};

const logToServerThrottled = throttle(LOG_INTERVAL, logToServer);

/**
 * Queue up a log to be sent to our backend log service (Splunk)
 */
const addLogEntry = (
    logLevel: string,
    message: string,
    extraInfo: Record<string, unknown> = {},
    currentMicroFE?: string,
) => {
    if (process.env.NODE_ENV !== "production") return;

    // @ts-expect-error: deprecated usage
    if (typeof message === "object" && message.status && message?.status >= 500) return;

    // @ts-expect-error: deprecated usage – filtering should happen earlier
    if (extraInfo.response?.meta?.code === 401) return;

    // Note that `category`, `level`, `content` etc. are whitelisted, known properties that the
    // backend endpoint will accept. You can't just put any property on the outermost nesting level.
    // However, the `content` property is just a JSON string, so we can add anything there.
    bufferedLogEntries = [
        ...bufferedLogEntries,
        {
            logLevel,
            message,
            clientTimestamp: dayjs().toISOString(),
            currentMicroFE,
            extraInfo,
        },
    ];

    logToServerThrottled();
};

export default addLogEntry;
